import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUPPORT_MAX_ATTACHMENTS } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { photoMessages } from '../i18n/supportLabels';
import { useNewTicket, useReasonPicker } from '../hooks/useSupport';
import { groupReasons } from '../hooks/supportMerge';
import {
  DeskNotice,
  ItemPicker,
  PhotoPicker,
  ReasonGroups,
  SupportDraftBar,
} from '../components/Support';
import { Button, EmptyState, ListSkeleton, Notice, PageHeader } from '../components/ui';
import { money } from '../utils/money';
import { useMyOrder } from '../hooks/useMyOrders';

/**
 * New help chat (W16): reason picker for the order (or general reasons), the order's items and
 * photos when the reason needs them, an optional note; then the chat opens with the assistant's
 * answer. An already open chat on the same order is offered instead of a second one.
 */
export function HelpNewPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('orderId');
  const r = useReasonPicker(orderId);
  const order = useMyOrder(orderId ?? '', { poll: false });
  const picker = r.picker;
  const f = useNewTicket(
    orderId,
    picker,
    {
      language: locale,
      onCreated: (view) => navigate(`/account/help/${view.id}`, { replace: true }),
    },
    { ...photoMessages(t), failed: t('help.createFailed') },
  );
  const country = order.order?.country ?? null;

  const header = (
    <PageHeader
      title={
        picker?.orderNumber
          ? t('help.newTitleOrder', { number: picker.orderNumber })
          : t('help.newTitle')
      }
      onBack={() => navigate(-1)}
      backLabel={t('common.back')}
    />
  );

  if (r.loading) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        <div className="px-4 pt-5">
          <ListSkeleton rows={5} />
        </div>
      </div>
    );
  }
  if (!picker) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        {r.notFound ? (
          <EmptyState icon="receipt" title={t('orders.notFound')} />
        ) : (
          <div className="px-4 pt-5">
            <Notice
              tone="negative"
              supportRef
              title={t('help.loadFailed')}
              action={
                <Button size="sm" tone="outline" onClick={r.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const groups = groupReasons(picker.reasons).map((group) => ({
    title: t(`help.group.${group.group}`),
    reasons: group.reasons.map((reason) => ({
      code: reason.code,
      label: reason.label,
      hint: reason.hint,
    })),
  }));
  const blocker = !f.reason
    ? null
    : f.missingItems
      ? t('help.needItems')
      : f.missingPhoto
        ? t('help.needPhoto')
        : f.photos.uploading
          ? t('help.uploading')
          : null;

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      {header}
      <div className="flex flex-1 flex-col gap-5 px-4 pt-4 pb-6">
        {picker.openTicketId && (
          <Notice
            tone="brand"
            title={t('help.openExisting')}
            action={
              <Button
                size="sm"
                onClick={() => navigate(`/account/help/${picker.openTicketId}`, { replace: true })}
              >
                {t('help.openChat')}
              </Button>
            }
          />
        )}
        {!picker.deskOpen && (
          <DeskNotice
            tone="warning"
            icon="clock"
            title={t('help.deskClosed')}
            body={t('help.botNote', { hours: picker.deskHoursText })}
          />
        )}
        {f.draftRestored && (
          <SupportDraftBar
            text={t('common.draftRestored')}
            keepLabel={t('common.keep')}
            discardLabel={t('common.discard')}
            onKeep={f.keepDraft}
            onDiscard={f.discardDraft}
          />
        )}

        <ReasonGroups
          groups={groups}
          value={f.reasonCode}
          onPick={f.setReason}
          label={t('help.reasonLabel')}
        />

        {f.reason?.needsItems && picker.items.length > 0 && (
          <ItemPicker
            title={t('help.items')}
            hint={t('help.itemsHint')}
            rows={picker.items.map((item) => ({
              id: item.orderItemId,
              name: item.name,
              ordered: item.quantity,
              picked: f.quantities[item.orderItemId] ?? 0,
              price: country ? money(country, item.lineTotalMinor) : '',
            }))}
            onChange={f.setQuantity}
            labels={{
              less: t('help.less'),
              more: t('help.more'),
              of: (picked, ordered) => t('help.itemOf', { picked, ordered }),
            }}
          />
        )}

        {f.reason && (
          <PhotoPicker
            title={t('help.photos')}
            hint={t('help.photosHint', { max: SUPPORT_MAX_ATTACHMENTS })}
            slots={f.photos.slots}
            canAdd={f.photos.canAdd}
            onFile={f.photos.add}
            onRemove={f.photos.remove}
            labels={{
              add: t('help.addPhoto'),
              remove: t('help.removePhoto'),
              failed: t('help.photoFailed'),
            }}
          />
        )}

        {f.reason && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-2">{t('help.note')}</span>
            <textarea
              value={f.message}
              rows={3}
              maxLength={f.maxLength}
              placeholder={t('help.notePlaceholder')}
              onChange={(event) => f.setMessage(event.target.value)}
              className="w-full resize-none rounded-field bg-surface px-3 py-2.5 text-sm text-ink shadow-[inset_0_0_0_1px_var(--color-field)] placeholder:text-ink-3 focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)] focus:outline-none"
            />
          </label>
        )}
      </div>
      <div className="sticky bottom-0 bg-surface pb-[env(safe-area-inset-bottom)] shadow-[inset_0_1px_0_var(--color-hair)]">
        <div className="px-4 py-3">
          {blocker && <p className="mb-2 text-center text-caption text-negative">{blocker}</p>}
          <Button full busy={f.submitting} disabled={!f.canSubmit} onClick={f.submit}>
            {t('help.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
